import { NetworkInterceptor } from './NetworkInterceptor';
import { AxiosInterceptor } from './AxiosInterceptor';
import { WebSocketConnector } from './WebSocketConnector';
import { IEnhancedNetworkRequest, IEnhancedNetworkResponse, INetworkInterceptorConfig } from './types';

export interface IRealTimeAPIInterceptorConfig extends Partial<INetworkInterceptorConfig> {
  websocket?: {
    url: string;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    enableHeartbeat?: boolean;
    heartbeatInterval?: number;
  };
  enableAxiosInterception?: boolean;
  autoConnect?: boolean;
  enableLogging?: boolean;
}

/**
 * 실시간 API 인터셉터 - 모든 네트워크 요청을 실시간으로 캡처하고 WebSocket으로 전송
 */
export class RealTimeAPIInterceptor {
  private _config: Required<Omit<IRealTimeAPIInterceptorConfig, 'onRequest' | 'onResponse' | 'onError'>> & {
    onRequest?: (request: IEnhancedNetworkRequest) => void;
    onResponse?: (response: IEnhancedNetworkResponse) => void;
    onError?: (error: Error, request: IEnhancedNetworkRequest) => void;
  };
  private _networkInterceptor: NetworkInterceptor;
  private _axiosInterceptor: AxiosInterceptor | null = null;
  private _webSocketConnector: WebSocketConnector | null = null;
  private _isActive: boolean = false;
  private _stats = {
    totalRequests: 0,
    totalResponses: 0,
    totalErrors: 0,
    startTime: 0,
  };

  constructor(config: IRealTimeAPIInterceptorConfig = {}) {
    this._config = {
      // NetworkInterceptor 기본 설정
      maxHistorySize: 1000,
      captureRequestBody: true,
      captureResponseBody: true,
      enableTiming: true,
      enableGraphQLParsing: true,
      enableAxiosInterception: true,
      maxBodySize: 1024 * 1024, // 1MB
      excludePatterns: [
        /chrome-extension:/,
        /moz-extension:/,
        /webkit-masked-url:/,
        /^data:/,
        /\.woff2?(\?|$)/,
        /\.ttf(\?|$)/,
        /favicon\.ico/,
        /sockjs/,
        /websocket/,
      ],
      includePatterns: [],
      onRequest: undefined,
      onResponse: undefined,
      onError: undefined,
      
      // RealTimeAPIInterceptor 설정
      websocket: {
        url: 'ws://localhost:8080/ws',
        reconnectInterval: 3000,
        maxReconnectAttempts: 10,
        enableHeartbeat: true,
        heartbeatInterval: 30000,
        ...config.websocket,
      },
      autoConnect: true,
      enableLogging: true,
      
      ...config,
    };

    // NetworkInterceptor 초기화
    this._networkInterceptor = new NetworkInterceptor({
      ...this._config,
      onRequest: this._handleNetworkRequest.bind(this),
      onResponse: this._handleNetworkResponse.bind(this),
      onError: this._handleNetworkError.bind(this),
    });

    // AxiosInterceptor 초기화
    if (this._config.enableAxiosInterception) {
      this._axiosInterceptor = new AxiosInterceptor({
        onRequest: this._handleNetworkRequest.bind(this),
        onResponse: this._handleNetworkResponse.bind(this),
        onError: this._handleNetworkError.bind(this),
      });
    }

    // WebSocketConnector 초기화
    if (this._config.websocket.url) {
      this._webSocketConnector = new WebSocketConnector(this._config.websocket);
    }

    this._log('RealTimeAPIInterceptor initialized');
  }

  /**
   * 실시간 API 인터셉션 시작
   */
  public async start(): Promise<void> {
    if (this._isActive) {
      this._log('Already active');
      return;
    }

    this._log('Starting real-time API interception...');
    this._stats.startTime = Date.now();

    try {
      // WebSocket 연결 먼저 시도 (autoConnect가 true인 경우)
      if (this._webSocketConnector && this._config.autoConnect) {
        try {
          await this._webSocketConnector.connect();
          this._log('WebSocket connected successfully');
        } catch (error) {
          this._log('WebSocket connection failed, continuing without real-time sync:', error);
        }
      }

      // 네트워크 인터셉션 시작
      this._networkInterceptor.startIntercepting();
      this._log('Network interception started');

      // Axios 인터셉션 시작 (활성화된 경우)
      if (this._axiosInterceptor) {
        this._axiosInterceptor.startIntercepting();
        this._log('Axios interception started');
      }

      this._isActive = true;
      this._log('Real-time API interception active');

      // 시작 이벤트 전송
      this._sendStartEvent();

    } catch (error) {
      this._log('Failed to start real-time API interception:', error);
      throw error;
    }
  }

  /**
   * 실시간 API 인터셉션 중지
   */
  public async stop(): Promise<void> {
    if (!this._isActive) {
      return;
    }

    this._log('Stopping real-time API interception...');

    try {
      // 네트워크 인터셉션 중지
      this._networkInterceptor.stopIntercepting();
      this._log('Network interception stopped');

      // Axios 인터셉션 중지
      if (this._axiosInterceptor) {
        this._axiosInterceptor.stopIntercepting();
        this._log('Axios interception stopped');
      }

      // 종료 이벤트 전송
      this._sendStopEvent();

      // WebSocket 연결 해제
      if (this._webSocketConnector) {
        this._webSocketConnector.disconnect();
        this._log('WebSocket disconnected');
      }

      this._isActive = false;
      this._log('Real-time API interception stopped');

    } catch (error) {
      this._log('Error while stopping real-time API interception:', error);
      throw error;
    }
  }

  /**
   * WebSocket 수동 연결
   */
  public async connectWebSocket(): Promise<void> {
    if (!this._webSocketConnector) {
      throw new Error('WebSocket connector not configured');
    }

    await this._webSocketConnector.connect();
    this._log('WebSocket manually connected');
  }

  /**
   * WebSocket 연결 해제
   */
  public disconnectWebSocket(): void {
    if (this._webSocketConnector) {
      this._webSocketConnector.disconnect();
      this._log('WebSocket manually disconnected');
    }
  }

  /**
   * 요청 히스토리 가져오기
   */
  public getRequestHistory(): IEnhancedNetworkRequest[] {
    return this._networkInterceptor.getRequestHistory();
  }

  /**
   * 응답 히스토리 가져오기
   */
  public getResponseHistory(): IEnhancedNetworkResponse[] {
    return this._networkInterceptor.getResponseHistory();
  }

  /**
   * 히스토리 초기화
   */
  public clearHistory(): void {
    this._networkInterceptor.clearHistory();
    this._stats = {
      totalRequests: 0,
      totalResponses: 0,
      totalErrors: 0,
      startTime: Date.now(),
    };
    this._log('History cleared');
  }

  /**
   * 통계 정보 가져오기
   */
  public getStats() {
    const networkMetrics = this._networkInterceptor.getNetworkMetrics();
    const uptime = Date.now() - this._stats.startTime;

    return {
      ...this._stats,
      uptime,
      networkMetrics,
      isActive: this._isActive,
      webSocketConnected: this._webSocketConnector?.isConnected || false,
      webSocketQueueSize: this._webSocketConnector?.queueSize || 0,
      axiosIntercepting: this._axiosInterceptor?.isIntercepting || false,
    };
  }

  /**
   * 현재 상태 정보
   */
  public getState() {
    return {
      isActive: this._isActive,
      config: { ...this._config },
      stats: this.getStats(),
      networkInterceptor: this._networkInterceptor.getState(),
      axiosInterceptor: this._axiosInterceptor?.getState(),
      webSocketConnector: this._webSocketConnector?.getState(),
    };
  }

  /**
   * 네트워크 요청 처리
   */
  private _handleNetworkRequest(request: IEnhancedNetworkRequest): void {
    this._stats.totalRequests++;
    
    this._log(`📤 Request: ${request.method} ${request.url}`, {
      id: request.id,
      timing: request.timing,
      size: request.size,
      graphql: request.graphql,
    });

    // WebSocket으로 실시간 전송
    if (this._webSocketConnector?.isConnected) {
      this._webSocketConnector.sendNetworkRequest(request);
    }
  }

  /**
   * 네트워크 응답 처리
   */
  private _handleNetworkResponse(response: IEnhancedNetworkResponse): void {
    this._stats.totalResponses++;

    this._log(`📥 Response: ${response.status} ${response.statusText}`, {
      id: response.id,
      requestId: response.requestId,
      timing: response.timing,
      size: response.size,
    });

    // WebSocket으로 실시간 전송
    if (this._webSocketConnector?.isConnected) {
      this._webSocketConnector.sendNetworkResponse(response);
    }
  }

  /**
   * 네트워크 에러 처리
   */
  private _handleNetworkError(error: Error, request: IEnhancedNetworkRequest): void {
    this._stats.totalErrors++;

    this._log(`❌ Error: ${error.message}`, {
      requestId: request.id,
      url: request.url,
      method: request.method,
      error: error.message,
    });

    // WebSocket으로 실시간 전송
    if (this._webSocketConnector?.isConnected) {
      this._webSocketConnector.sendError(error, request);
    }
  }

  /**
   * 시작 이벤트 전송
   */
  private _sendStartEvent(): void {
    if (this._webSocketConnector?.isConnected) {
      this._webSocketConnector.sendNetworkRequest({
        id: `start_${Date.now()}`,
        url: 'debug-time-machine://interceptor/start',
        method: 'EVENT',
        headers: {},
        timestamp: Date.now(),
        timing: {
          startTime: performance.now(),
          requestStart: performance.now(),
          responseStart: 0,
          responseEnd: 0,
          duration: 0,
        },
        size: {
          requestHeaders: 0,
          requestBody: 0,
          total: 0,
        },
        priority: 'high',
      } as IEnhancedNetworkRequest);
    }
  }

  /**
   * 종료 이벤트 전송
   */
  private _sendStopEvent(): void {
    if (this._webSocketConnector?.isConnected) {
      this._webSocketConnector.sendNetworkRequest({
        id: `stop_${Date.now()}`,
        url: 'debug-time-machine://interceptor/stop',
        method: 'EVENT',
        headers: {},
        timestamp: Date.now(),
        timing: {
          startTime: performance.now(),
          requestStart: performance.now(),
          responseStart: 0,
          responseEnd: 0,
          duration: 0,
        },
        size: {
          requestHeaders: 0,
          requestBody: 0,
          total: 0,
        },
        priority: 'high',
      } as IEnhancedNetworkRequest);
    }
  }

  /**
   * 로깅 헬퍼
   */
  private _log(message: string, data?: unknown): void {
    if (!this._config.enableLogging) {
      return;
    }

    const timestamp = new Date().toISOString();
    console.log(`[RealTimeAPIInterceptor] ${timestamp} ${message}`, data || '');
  }

  /**
   * 정리
   */
  public async destroy(): Promise<void> {
    this._log('Destroying RealTimeAPIInterceptor...');
    
    await this.stop();
    
    this._networkInterceptor.destroy();
    this._axiosInterceptor?.destroy();
    this._webSocketConnector?.destroy();
    
    this._log('RealTimeAPIInterceptor destroyed');
  }
}

// 전역 인스턴스 (선택적)
let globalInterceptor: RealTimeAPIInterceptor | null = null;

/**
 * 전역 API 인터셉터 생성/가져오기
 */
export function getGlobalAPIInterceptor(config?: IRealTimeAPIInterceptorConfig): RealTimeAPIInterceptor {
  if (!globalInterceptor) {
    globalInterceptor = new RealTimeAPIInterceptor(config);
  }
  return globalInterceptor;
}

/**
 * 전역 API 인터셉터 정리
 */
export async function destroyGlobalAPIInterceptor(): Promise<void> {
  if (globalInterceptor) {
    await globalInterceptor.destroy();
    globalInterceptor = null;
  }
}
